
import { useState, useEffect } from "react";
import { SimpleTag, TagCondition } from "../types/categoryRules";
import { ConditionOperatorSelect } from "./ConditionOperatorSelect";
import { LogicOperatorSelect } from "./LogicOperatorSelect";
import { TagBadge } from "./TagBadge";
import { TagSelector } from "./TagSelector";
import { ExceptionSection } from "./ExceptionSection";
import { AIDescriptionPanel } from "./AIDescriptionPanel";

interface RuleConditionBuilderProps {
  categoryId: string;
  tags: SimpleTag[];
  conditions: TagCondition[];
  onAddCondition: (categoryId: string) => void;
  onUpdateCondition: (categoryId: string, conditionId: string, updates: Partial<TagCondition>) => void;
  onRemoveCondition: (categoryId: string, conditionId: string) => void;
}

export const RuleConditionBuilder = ({ 
  categoryId, 
  tags, 
  conditions, 
  onAddCondition, 
  onUpdateCondition, 
  onRemoveCondition 
}: RuleConditionBuilderProps) => {
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  // Automatically create the first condition if none exist
  useEffect(() => {
    if (conditions.length === 0) {
      onAddCondition(categoryId);
    }
  }, [conditions.length, categoryId, onAddCondition]);

  // Auto-generate explanation whenever the rule changes
  useEffect(() => {
    if (conditions.length > 0) {
      const condition = conditions[0];
      if (condition.selectedTagIds && condition.selectedTagIds.length > 0 && condition.ruleType) {
        generateExplanation(condition);
      }
    }
  }, [conditions]);

  if (tags.length === 0) {
    return (
      <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200 mt-3">
        <p className="text-sm text-gray-500">
          No tags available in this category. Create tags to build custom rules.
        </p>
      </div>
    );
  }

  const generateExplanation = (condition: TagCondition) => {
    const selectedTags = tags.filter(tag => condition.selectedTagIds.includes(tag.id));
    if (selectedTags.length === 0) {
      return;
    }

    const tagNames = selectedTags.map(tag => `"${tag.name}"`).join(', ');
    const logicText = condition.logicOperator === 'all_of' ? 'all of' : 'any of';

    // Generate AI explanation based on rule configuration
    const explanations = {
      exclude_with: `This rule will allow all apps except those tagged with ${logicText} ${tagNames}. Users will have access to everything except apps with ${condition.logicOperator === 'all_of' ? 'all of these tags' : 'any of these tags'}.`,
      only_with: `This rule will only allow apps that are specifically tagged with ${logicText} ${tagNames}. Apps must have ${condition.logicOperator === 'all_of' ? 'all of these tags' : 'at least one of these tags'} to be accessible.`
    };

    // Add exception handling to explanation
    let explanation = explanations[condition.ruleType as keyof typeof explanations] || 'Rule explanation will be generated based on your selection.';
    
    if (condition.exceptionTagIds && condition.exceptionTagIds.length > 0) {
      const exceptionTags = tags.filter(tag => condition.exceptionTagIds!.includes(tag.id));
      const exceptionTagNames = exceptionTags.map(tag => `"${tag.name}"`).join(', ');
      const exceptionLogicText = condition.exceptionLogicOperator === 'all_of' ? 'all of' : 'any of';
      explanation += ` However, apps that also contain ${exceptionLogicText} ${exceptionTagNames} will be blocked even if they meet the main criteria.`;
    }

    onUpdateCondition(categoryId, condition.id, { explanation });
  };

  const toggleTagSelection = (conditionId: string, tagId: string, isException = false) => {
    const condition = conditions.find(c => c.id === conditionId);
    if (!condition) return;

    const currentTagIds = isException ? (condition.exceptionTagIds || []) : (condition.selectedTagIds || []);
    const newTagIds = currentTagIds.includes(tagId)
      ? currentTagIds.filter(id => id !== tagId)
      : [...currentTagIds, tagId];

    const updates = isException 
      ? { exceptionTagIds: newTagIds }
      : { selectedTagIds: newTagIds };

    onUpdateCondition(categoryId, conditionId, updates);

    // Auto-close dropdown after selection
    const key = isException ? `${conditionId}-exception` : conditionId;
    setOpenDropdowns(prev => ({
      ...prev,
      [key]: false
    }));
  };

  const removeTag = (conditionId: string, tagId: string, isException = false) => {
    const condition = conditions.find(c => c.id === conditionId);
    if (!condition) return;

    const currentTagIds = isException ? (condition.exceptionTagIds || []) : (condition.selectedTagIds || []);
    const newTagIds = currentTagIds.filter(id => id !== tagId);

    const updates = isException 
      ? { exceptionTagIds: newTagIds }
      : { selectedTagIds: newTagIds };

    onUpdateCondition(categoryId, conditionId, updates);
  };

  const toggleDropdown = (conditionId: string, isException = false) => {
    const key = isException ? `${conditionId}-exception` : conditionId;
    setOpenDropdowns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getTagWord = (condition: TagCondition, isException = false) => {
    const selectedCount = isException 
      ? (condition.exceptionTagIds?.length || 0)
      : (condition.selectedTagIds?.length || 0);
    return selectedCount === 1 ? 'tag' : 'tags';
  };

  const addException = (conditionId: string) => {
    console.log('Adding exception for condition:', conditionId);
    onUpdateCondition(categoryId, conditionId, { 
      exceptionTagIds: [],
      exceptionLogicOperator: 'any_of'
    });
  };

  const removeException = (conditionId: string) => {
    console.log('Removing exception for condition:', conditionId);
    onUpdateCondition(categoryId, conditionId, { 
      exceptionTagIds: [],
      exceptionLogicOperator: undefined
    });
  };

  const updateExceptionLogicOperator = (conditionId: string, operator: 'any_of' | 'all_of') => {
    console.log('Updating exception logic operator for condition:', conditionId, 'to:', operator);
    onUpdateCondition(categoryId, conditionId, { exceptionLogicOperator: operator });
  };

  // Since we only have one condition per category, get the first (and only) condition
  const condition = conditions[0];
  if (!condition) return null;

  const hasOperatorSelection = Boolean(condition.ruleType);

  console.log('Current condition:', {
    ruleType: condition.ruleType,
    selectedTagIds: condition.selectedTagIds,
    logicOperator: condition.logicOperator,
    exceptionTagIds: condition.exceptionTagIds,
    exceptionLogicOperator: condition.exceptionLogicOperator
  });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
          <ConditionOperatorSelect
            value={condition.ruleType || ''}
            onValueChange={(value) => {
              console.log('Operator changed to:', value);
              onUpdateCondition(categoryId, condition.id, { 
                ruleType: value as TagCondition['ruleType']
              });
            }}
          />

          {hasOperatorSelection && (
            <>
              <span>apps that contain</span>

              <LogicOperatorSelect
                value={condition.logicOperator || 'any_of'}
                onValueChange={(value) => {
                  console.log('Logic operator changed to:', value);
                  onUpdateCondition(categoryId, condition.id, { 
                    logicOperator: value as 'any_of' | 'all_of'
                  });
                }}
              />

              <span>the following {getTagWord(condition)}:</span>

              <div className="flex items-center gap-1 flex-wrap">
                {/* Display selected tags inline */}
                {condition.selectedTagIds && condition.selectedTagIds.length > 0 && condition.selectedTagIds.map(tagId => {
                  const tag = tags.find(t => t.id === tagId);
                  if (!tag) return null;
                  return (
                    <TagBadge
                      key={tagId}
                      tag={tag}
                      onRemove={() => removeTag(condition.id, tagId)}
                    />
                  );
                })}

                {/* Tag selector for main tags */}
                <TagSelector
                  tags={tags}
                  selectedTagIds={condition.selectedTagIds || []}
                  onToggleTag={(tagId) => toggleTagSelection(condition.id, tagId)}
                  isOpen={openDropdowns[condition.id] || false}
                  onToggle={() => toggleDropdown(condition.id)}
                  className={!hasOperatorSelection ? 'border-red-500' : ''}
                />

                {/* Exception section */}
                <ExceptionSection
                  condition={condition}
                  tags={tags}
                  onAddException={() => addException(condition.id)}
                  onRemoveException={() => removeException(condition.id)}
                  onToggleTag={(tagId) => toggleTagSelection(condition.id, tagId, true)}
                  onRemoveTag={(tagId) => removeTag(condition.id, tagId, true)}
                  isDropdownOpen={openDropdowns[`${condition.id}-exception`] || false}
                  onToggleDropdown={() => toggleDropdown(condition.id, true)}
                  onUpdateExceptionLogicOperator={(operator) => updateExceptionLogicOperator(condition.id, operator)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Explanation - automatically displayed when rule has tags and operator */}
      {condition.selectedTagIds && condition.selectedTagIds.length > 0 && condition.explanation && hasOperatorSelection && (
        <AIDescriptionPanel explanation={condition.explanation} />
      )}
    </div>
  );
};
